import { useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../../../lib/api.js';
import { aiClient, AiBudgetExhaustedError } from './lib/aiClient.js';
import {
  CATEGORY_ORDER,
  CATEGORY_PALETTE,
  CATEGORY_FALLBACK,
} from './lib/categoryPalette.js';
import ReflectionInline from './ReflectionInline.jsx';

// Post-send debrief screen.
//
// Three things the editor cares about once the brief is out the door:
//   1. AI "Day Diagnosis" — a paragraph (John's voice) that compares pipeline
//      delivery to final ship and ends with a one-line "Tomorrow watch".
//      Drafted automatically; editor can refine and regenerate.
//   2. What Happened — derived data panel (counts) so the editor can sanity-
//      check the diagnosis against the actual numbers.
//   3. Reflection — didGood / didBad / lookouts chips (reuses ReflectionInline).
//
// Diagnosis text persists to BriefPulse.changeSummary; reflection persists to
// BriefPulse.reflection. Both share the same PUT /api/brief-pulse/:isoDate.
export default function BriefComplete({
  isoDate,
  edition,
  pulse,
  onPulseChange,
  feedback,
  publishResult,
  onBackToEditor,
}) {
  const inventory = useMemo(() => buildInventory(edition, feedback), [edition, feedback]);

  // Diagnosis: text + tomorrow-watch + edited flag, all sourced from the
  // BriefPulse.changeSummary row if one already exists for the day.
  const existing = pulse?.changeSummary || null;
  const seeded = parseStoredDiagnosis(existing?.text);
  const [diagBody, setDiagBody] = useState(seeded.body);
  const [diagWatch, setDiagWatch] = useState(seeded.watch);
  const [diagEdited, setDiagEdited] = useState(!!existing?.edited);
  const [diagMeta, setDiagMeta] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState(null);
  const [savingDiag, setSavingDiag] = useState(false);
  const lastSavedRef = useRef(existing?.text || '');

  // Reflection chips — local state, debounced upstream so we don't fire a PUT
  // per keystroke / chip add. ReflectionInline keeps its own local input draft.
  const [reflection, setReflection] = useState({
    didGood: pulse?.reflection?.didGood || [],
    didBad: pulse?.reflection?.didBad || [],
    lookouts: pulse?.reflection?.lookouts || [],
  });
  const reflectionTimer = useRef(null);

  // Auto-draft on mount if we don't already have a diagnosis stored. The
  // editor can always regenerate manually. We track the request via a ref so
  // a strict-mode double-mount doesn't fire it twice.
  const draftedOnce = useRef(false);
  useEffect(() => {
    if (draftedOnce.current) return;
    if (existing?.text && existing.text.trim().length > 0) return;
    draftedOnce.current = true;
    runDiagnosis({ silent: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runDiagnosis({ silent }) {
    setDrafting(true);
    setDraftError(null);
    try {
      const data = await aiClient.changeSummary({ isoDate });
      const body = data.body || data.text || '';
      const watch = data.tomorrowWatch || '';
      setDiagBody(body);
      setDiagWatch(watch);
      setDiagEdited(false);
      setDiagMeta({
        model: data.model || '',
        latencyMs: data.latencyMs || 0,
        inventory: data.inventory || inventory,
      });
      if (!silent) {
        await persistDiagnosis({ body, watch, edited: false });
      }
    } catch (err) {
      if (err instanceof AiBudgetExhaustedError) {
        setDraftError('AI budget exhausted. The diagnosis card is read-only until the cap resets.');
      } else {
        setDraftError(err?.message || 'Failed to draft diagnosis.');
      }
    } finally {
      setDrafting(false);
    }
  }

  async function persistDiagnosis({ body, watch, edited }) {
    const text = composeDiagnosis(body, watch);
    if (text === lastSavedRef.current) return;
    setSavingDiag(true);
    try {
      const data = await api.put(`/brief-pulse/${isoDate}`, {
        changeSummary: { text, edited, flagged: false },
      });
      lastSavedRef.current = text;
      onPulseChange?.(data.pulse);
    } catch (err) {
      setDraftError(err?.message || 'Failed to save diagnosis.');
    } finally {
      setSavingDiag(false);
    }
  }

  function handleDiagBlur() {
    persistDiagnosis({ body: diagBody, watch: diagWatch, edited: diagEdited });
  }

  function handleReflectionChange(next) {
    setReflection(next);
    if (reflectionTimer.current) clearTimeout(reflectionTimer.current);
    reflectionTimer.current = setTimeout(async () => {
      try {
        const data = await api.put(`/brief-pulse/${isoDate}`, {
          reflection: next,
        });
        onPulseChange?.(data.pulse);
      } catch (err) {
        // Non-fatal — the local state still reflects the intent.
        // eslint-disable-next-line no-console
        console.error('[brief-complete] reflection save failed', err);
      }
    }, 600);
  }

  // Pulse stats from the editing surface (initialScore / nowScore / effort /
  // comment). We just display them here — they were captured pre-send.
  const initialScore = pulse?.initialScore ?? null;
  const nowScore = pulse?.nowScore ?? null;
  const effort = pulse?.effort || null;
  const briefDate = formatBriefDate(isoDate);
  const articleCount = inventory.shipping_count;
  const ratedCount = feedback?.ratedCount || 0;

  return (
    <div className="max-w-[840px] mx-auto pt-8 pb-20">
      {/* Confirmation strip — mode-aware: "sent" for send-now, "scheduled" for schedule. */}
      <div className="hxd-card mb-5 border-l-4" style={{ borderLeftColor: '#15803D' }}>
        <div className="flex flex-wrap items-start gap-4">
          <div className="text-[36px] leading-none" aria-hidden="true">
            {publishResult?.mode === 'schedule' ? '🕒' : '✓'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-[11px] font-bold tracking-display-md uppercase mb-1" style={{ color: '#15803D' }}>
              {publishResult?.mode === 'schedule' ? 'Brief scheduled' : 'Brief sent'}
            </div>
            <h1 className="hxd-page-title leading-tight mb-1">
              {briefDate ? `${briefDate} brief handed off.` : `${isoDate} brief handed off.`}
            </h1>
            <p className="text-[12px] text-hxd-muted">
              {publishResult?.mode === 'schedule'
                ? `${articleCount} ${articleCount === 1 ? 'article' : 'articles'} scheduled in Beehiiv${publishResult?.edition?.scheduledFor ? ` for ${formatScheduleStamp(publishResult.edition.scheduledFor)}` : ''}. Subscribers receive the email then.`
                : `${articleCount} ${articleCount === 1 ? 'article' : 'articles'} published to Beehiiv. Subscribers receive the email shortly.`}
              {publishResult?.downgraded && (
                <>
                  {' '}
                  <span className="text-[#7a5b13]">
                    (Non-production: pushed as draft only — no real send.)
                  </span>
                </>
              )}
            </p>
            {publishResult?.beehiivPostUrl && (
              <a
                href={publishResult.beehiivPostUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 text-[12px] font-semibold text-hxd-orange hover:underline"
              >
                Open in Beehiiv →
              </a>
            )}
          </div>
          <button
            type="button"
            onClick={onBackToEditor}
            className="hxd-btn hxd-btn-ghost hxd-btn-sm self-start"
          >
            ← Dashboard
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-5">
          <Stat
            label="Pipeline score"
            value={initialScore ? `${initialScore}/10` : '—'}
          />
          <Stat
            label="Post-edit score"
            value={nowScore ? `${nowScore}/10` : '—'}
          />
          <Stat
            label="Effort"
            value={effort ? capitalize(effort) : '—'}
          />
          <Stat
            label="Rated"
            value={articleCount ? `${ratedCount}/${articleCount}` : '—'}
          />
        </div>
      </div>

      {/* AI Day Diagnosis */}
      <section className="hxd-card mb-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <div className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-orange mb-1">
              Day diagnosis · AI
            </div>
            <p className="text-[12px] text-hxd-muted">
              Drafted in John's voice from today's edit log and the pipeline-to-ship delta.
              Edit freely — yours overrides the draft.
            </p>
          </div>
          <button
            type="button"
            onClick={() => runDiagnosis({ silent: false })}
            disabled={drafting}
            className="hxd-btn hxd-btn-ghost hxd-btn-sm whitespace-nowrap"
          >
            {drafting ? 'Drafting…' : '↻ Regenerate'}
          </button>
        </div>

        {drafting && !diagBody ? (
          <div className="text-[12px] text-hxd-muted italic">
            Reading today's edits and your ratings…
          </div>
        ) : (
          <>
            <textarea
              className="hxd-input w-full font-sans"
              style={{ minHeight: 120, lineHeight: 1.55 }}
              value={diagBody}
              onChange={(e) => {
                setDiagBody(e.target.value);
                setDiagEdited(true);
              }}
              onBlur={handleDiagBlur}
              placeholder="Day diagnosis will appear here once drafting completes."
            />
            <div className="mt-3">
              <label className="hxd-label flex items-center justify-between">
                <span>Tomorrow watch</span>
                {diagEdited && (
                  <span className="text-[10px] text-hxd-orange font-bold uppercase tracking-display-sm">
                    Edited
                  </span>
                )}
              </label>
              <input
                className="hxd-input"
                value={diagWatch}
                onChange={(e) => {
                  setDiagWatch(e.target.value);
                  setDiagEdited(true);
                }}
                onBlur={handleDiagBlur}
                placeholder="e.g. FAA NPRM expected Friday; Anduril cruise-missile follow-on."
              />
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-hxd-muted">
              {diagMeta?.model && <span>· {diagMeta.model}</span>}
              {diagMeta?.latencyMs ? <span>· {diagMeta.latencyMs}ms</span> : null}
              {savingDiag && <span>· Saving…</span>}
              {draftError && (
                <span className="text-hxd-err">· {draftError}</span>
              )}
            </div>
          </>
        )}
      </section>

      {/* What happened — derived data */}
      <section className="hxd-card mb-5">
        <div className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-orange mb-3">
          What happened
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
          <Stat label="Shipped" value={inventory.shipping_count} />
          <Stat label="Cut from pipeline" value={inventory.cut_from_pipeline} />
          <Stat label="Promoted from bench" value={inventory.editor_promoted_from_bench} />
          <Stat label="Rewrites accepted" value={inventory.rewrites_accepted} />
          <Stat label="Claims verified" value={inventory.claims_verified} />
        </div>

        {inventory.category_mix_rows.length > 0 && (
          <div>
            <div className="font-display text-[10px] font-bold tracking-display-md uppercase text-hxd-muted mb-2">
              Category mix
            </div>
            <div className="flex flex-wrap gap-1.5">
              {inventory.category_mix_rows.map(({ category, count }) => {
                const palette = CATEGORY_PALETTE[category] || CATEGORY_FALLBACK;
                return (
                  <span
                    key={category}
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-[3px] text-[11px] font-bold"
                    style={{
                      backgroundColor: palette.track,
                      color: palette.ink,
                      border: `1px solid ${palette.bar}`,
                    }}
                  >
                    <span>{category}</span>
                    <span className="font-mono">{count}</span>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {inventory.cut_reasons.length > 0 && (
          <div className="mt-4 pt-4 border-t border-hxd-border-soft">
            <div className="font-display text-[10px] font-bold tracking-display-md uppercase text-hxd-muted mb-2">
              Cut reasons ({inventory.cut_reasons.length})
            </div>
            <ul className="space-y-1 text-[12px] text-hxd-ink">
              {inventory.cut_reasons.slice(0, 6).map((reason, i) => (
                <li key={i} className="leading-relaxed">
                  <span className="text-hxd-err mr-1.5">✗</span>
                  {reason}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Reflection */}
      <section className="hxd-card mb-5">
        <div className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-orange mb-1">
          Reflection
        </div>
        <p className="text-[12px] text-hxd-muted mb-4">
          Short notes for tomorrow's editor (you). Three buckets, one-liner chips.
        </p>
        <ReflectionInline reflection={reflection} onChange={handleReflectionChange} />
      </section>

      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] text-hxd-muted">
          Reflection + diagnosis save automatically to your pulse log.
        </span>
        <button
          type="button"
          onClick={onBackToEditor}
          className="hxd-btn hxd-btn-primary hxd-btn-sm"
        >
          ← Back to dashboard
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-[3px] border border-hxd-border-soft bg-white px-2.5 py-1.5">
      <div className="font-display text-[10px] font-bold tracking-display-sm uppercase text-hxd-muted">
        {label}
      </div>
      <div className="font-display text-[15px] font-bold text-hxd-ink">{value}</div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function capitalize(s) {
  if (!s) return '';
  return s[0].toUpperCase() + s.slice(1);
}

function formatBriefDate(isoDate) {
  if (!isoDate) return '';
  const [y, m, d] = String(isoDate).split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

// Pretty-print an ISO timestamp in the user's local timezone for the
// "scheduled for X" confirmation copy.
function formatScheduleStamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Round-trip: combine the editor's body + tomorrow-watch into one stored
// string (so the BriefPulse.changeSummary.text field stays the canonical
// home), then split them back on read.
function composeDiagnosis(body, watch) {
  const b = (body || '').trim();
  const w = (watch || '').trim();
  if (!b && !w) return '';
  if (!w) return b;
  if (!b) return `Tomorrow watch: ${w}`;
  return `${b}\n\nTomorrow watch: ${w}`;
}

function parseStoredDiagnosis(text) {
  if (!text) return { body: '', watch: '' };
  const m = text.match(/(?:^|\n)\s*Tomorrow watch:\s*(.+?)\s*$/i);
  if (!m) return { body: text.trim(), watch: '' };
  return { body: text.slice(0, m.index).trim(), watch: m[1].trim() };
}

function buildInventory(edition, feedback) {
  const articles = Array.isArray(edition?.articles) ? edition.articles : [];
  // Everything still in Edition.articles is shipping; the pipeline status is
  // an informational signal only (kept here so the inventory still surfaces
  // a "pipeline marked auto_failed" count for the editor's awareness).
  const shipping = articles.filter((a) => !!a);
  const cutFromPipeline = articles.filter((a) => a && a.status === 'auto_failed').length;
  const editorPromoted = shipping.filter((a) => a.editorPromoted === true).length;

  const counts = new Map();
  for (const a of shipping) {
    const k = a.category || 'Uncategorized';
    counts.set(k, (counts.get(k) || 0) + 1);
  }
  const category_mix_rows = []
    .concat(CATEGORY_ORDER.filter((c) => counts.has(c)))
    .concat([...counts.keys()].filter((c) => !CATEGORY_ORDER.includes(c)))
    .map((category) => ({ category, count: counts.get(category) }));

  const byId = feedback?.byArticleId || {};
  let rewriteAccepted = 0;
  let verifyCount = 0;
  const cutReasons = [];
  for (const row of Object.values(byId)) {
    if (row?.toolUsed === 'rewrite') rewriteAccepted += 1;
    if (row?.toolUsed === 'verify') verifyCount += 1;
    if (row?.q1Answer === 'bad_stopped') {
      const reason = (row.branchAnswer || row.followupAnswer || '').trim();
      if (reason) cutReasons.push(reason);
    }
  }

  return {
    shipping_count: shipping.length,
    cut_from_pipeline: cutFromPipeline,
    editor_promoted_from_bench: editorPromoted,
    rewrites_accepted: rewriteAccepted,
    claims_verified: verifyCount,
    category_mix_rows,
    cut_reasons: cutReasons,
  };
}
